﻿import {ViewContainerRef, EventEmitter} from "@angular/core";
import {INotifyError, ErrorNotification, ErrorAction,  } from "../../validation/iNotifyError";
import {ValidationMessage} from "../../validation/validationMessage";
import {MandatoryDataMissing, IncorrectFormat} from "../../validation/validationConstants";
import {ValidationMessageHelper} from "../../validation/validationMessageHelper";
import {ValidateResult} from "../../validation/validateResult";
import {RequiredValidator} from "../../validation/validators/requiredValidator";
import {NumericValidator, NumericValidatorType} from "../../validation/validators/numericValidator";
import {IValidate} from "../../validation/iValidate";
import {IValidator, ValidatorTuple} from "../../validation/validators/iValidator";

export abstract class TextBoxBase {
    public isMandatory: boolean;
    public inputValue: any;
    public isDisabled: boolean;
    public isReadOnly: boolean;
    public propertyName: string;
    public propertyDisplayName: string;
    public validate = true;
    public maxLength=150;
    public validators: ValidatorTuple[];
    public errorMessage: string;
    public hasError: boolean;

    private _isNumeric: boolean;
    private _isInt: boolean;
    private _isPositiveInt: boolean;
    
    private inputErrorTooltipResourceKey: string;
    private inputErrorTextResourceKey: string;

    private _notifyErrorObject: INotifyError;
    private _notifier: EventEmitter<ErrorNotification>;
    private _validationMessages: ValidationMessage[] = [];
    private _inputType = "text";

    constructor(private elemRef: ViewContainerRef) { }

    public get type(): string {
        return this._inputType;
    }

    public set type(value) {
        if (value === "decimal" || value === "int" || value === "number" || value==="+int") {
            this._isNumeric = true;
            this._inputType = "number";
            this._isInt = value === "int" || value === "+int";
            this._isPositiveInt = value === "+int";
            if (this._isInt || this._isPositiveInt)
                this.maxLength = 10;
            else
                this.maxLength = 18;
        }
        else if (value)
            this._inputType = value;
    }

    public set iNotifyErrorObject(value: INotifyError) {
        if (value) {
            this._notifyErrorObject = value;
            this.initializeListener(value.notifier);
        } else {
            this._notifyErrorObject = null;
            this.initializeListener(null);
        }
    };

    public validateValue() {

        //validate only numeric or text , do not validate of type like password etc
        if (!this._notifyErrorObject ||!this._isNumeric && !(this.type === "text") && !this.validate)
            return;

        //this is for numeric value because number input gives empty string if value is not number
        let value = !this._isNumeric ? this.inputValue : this.getTextBoxValue();

        this.beforeValidate();
        let validateResult = new ValidateResult();
        let messageToRemove: string;
        if (this.isMandatory && this.isMandatory.toString()=="true") {
            validateResult = new RequiredValidator().validate(value, this.propertyName, this.propertyDisplayName,"");
            if (!validateResult.isValid)
                messageToRemove = MandatoryDataMissing;
        }

        //if there is Mandatory message already do not validate further
        if (this._isNumeric && validateResult.isValid) {
            let type = this._isInt
                ? (this._isPositiveInt ? NumericValidatorType.PositiveInt : NumericValidatorType.Int)
                : NumericValidatorType.Decimal;
            validateResult = new NumericValidator().validate(value, this.propertyName, this.propertyDisplayName,"", type);
            if (!validateResult.isValid)
                messageToRemove = IncorrectFormat;
        }

        //validate validators
        if (validateResult.isValid && this.validators && this.validators.length > 0) {
            this.validators.forEach(validator => {
                if (!validateResult.isValid)
                    return;
                else {
                    validateResult = validator.validator.validate(value, this.propertyName, this.propertyDisplayName,"", validator.arg);
                }
            });
        }

        if (this.type === "phone" && validateResult.isValid && !isNaN(value)) {
            this.inputValue = value.substring(0, 3) + "-" + value.substring(3, 6) + "-" + value.substring(6, 10);
        }
        else if (this.type === "phone" && validateResult.isValid) {
                    this.inputValue = value.replace(")-", ") ");
        }

        this.afterValidate(validateResult);
        this.applyError(validateResult, messageToRemove);
    }

    public dispose() {
        if (this._notifier)
            this._notifier.unsubscribe();
    }

    public isIValidate(): boolean {
        if (this._notifyErrorObject) {
            let obj = <any>this._notifyErrorObject;
            return obj.afterValidate && obj.afterValidate;
        }
        return false;
    }
    
    private applyError(validateResult: ValidateResult, messageTextLocalizationId) {
        if (validateResult && !validateResult.isValid) {
            this.removeAllError();
            this._notifyErrorObject.addValidationMessage(validateResult.validationMessage, false);
            this.setErrors(new ErrorNotification(Array.isArray(validateResult.validationMessage) ? validateResult.validationMessage[0] : validateResult.validationMessage, ErrorAction.Added));
        }
        else if(messageTextLocalizationId) {
            this.removeError(messageTextLocalizationId);
        }
        else {
            this.removeAllError();
        }
    }

    private setErrors(er: ErrorNotification) {
        //clear
        if (er.action === ErrorAction.Clear) {
            this.setError(null);
            this._validationMessages = [];
        }

        if (!er.validationMessage || er.validationMessage.propertyName !== this.propertyName)
            return;
        //add
        else if (er.action === ErrorAction.Added && er.validationMessage) {
            this.setError(er.validationMessage);
            this._validationMessages.push(er.validationMessage);
        }
        //remove
        else if (er.action === ErrorAction.Removed && er.validationMessage) {
            this._validationMessages = ValidationMessageHelper.removeMessage(this._validationMessages, er.validationMessage);
            this._validationMessages.length > 0 ? this.setError(this._validationMessages[0]) : this.setError(null);
        }
    }

    private setError(vm: ValidationMessage) {
        if (!vm) {
            this.hasError = false;
            this.errorMessage = null;
        }
        else {
            this.hasError = true;
            this.errorMessage = vm.message;
        }
    }

    private removeAllError() {
        if (this._validationMessages.length > 0) {
            this._notifyErrorObject.removeValidationMessage(this._validationMessages, false);
            this._validationMessages = [];
        }
        this.setError(null);
    }

    private removeError(messageTextResId) {
        let errorsToRemove = this._validationMessages.filter(s => s.message === messageTextResId);
        if (errorsToRemove.length > 0) {
            this._notifyErrorObject.removeValidationMessage(errorsToRemove, false);
            errorsToRemove.forEach(vm => ValidationMessageHelper.removeMessage(this._validationMessages, vm));
        }
        if (this._validationMessages.length > 0)
            this.setError(this._validationMessages[0]);
        else
            this.setError(null);
    }

    private initializeListener(value) {
        if (this._notifier)
            this._notifier.unsubscribe();
        this._notifier = value;
        if (value) {
            this._notifier.subscribe(s => this.setErrors(s));
        }
    }

    private getTextBoxValue() {
        if (this.inputValue)
            return this.inputValue;
        let isbadInput = this.elemRef.element.nativeElement.getElementsByTagName("input")[0].validity.badInput;
        return isbadInput ? "dummy" : this.inputValue;
    }

    private beforeValidate() {
        if (this.isIValidate()) {
            let obj = <any>this._notifyErrorObject;
            (<IValidate>obj).beforeValidate(this.propertyName);
        }
    }

    private afterValidate(result: ValidateResult) {
        if (this.isIValidate()) {
            let obj = <any>this._notifyErrorObject;
            (<IValidate>obj).afterValidate(result?result.validationMessage:null);
        }
    }

}